/**
 * Thread @-mentions — feature flag (group-threading P2b).
 *
 * PLATFORM DEFAULT ON (owner directive 2026-08-17, completing the all-accounts
 * rollout approved 2026-07-24). The compile default is consulted AFTER the
 * blank-account guard — it acts like a '*' allowlist token, so anonymous /
 * not-yet-resolved sessions stay fail-closed. Rollback: flip this constant to
 * false and the env CSV allowlist governs again. Independent of every other
 * flag. Gates the on-send @-mention summon and the composer autocomplete.
 */
export const THREAD_MENTIONS_ENABLED: boolean = true;

/** Build-time env var (comma-separated account ids) naming the enabled accounts. */
export const THREAD_MENTIONS_ACCOUNTS_ENV = 'REACT_APP_THREAD_MENTIONS_ACCOUNTS';

export interface MentionsEnablementContext {
  accountId?: string | null;
  override?: boolean;
  enabledAccountIds?: Iterable<string>;
  /** Test seam for the compile default (rollback-path tests). Production callers never pass this. */
  defaultEnabled?: boolean;
}

/** Parse the account allowlist from a build-time env map (direct `process.env` read). */
export function readEnabledMentionAccountsFromEnv(
  env?: Record<string, string | undefined>,
): string[] {
  const raw =
    (env
      ? env[THREAD_MENTIONS_ACCOUNTS_ENV]
      : process.env.REACT_APP_THREAD_MENTIONS_ACCOUNTS) ?? '';
  const seen = new Set<string>();
  for (const part of String(raw).split(',')) {
    const id = part.trim();
    if (id) seen.add(id);
  }
  return [...seen];
}

/**
 * Account-scoped resolver. Precedence: explicit override → blank-account
 * fail-closed guard → (ON) compile default → env CSV allowlist. The blank guard
 * sits ABOVE the compile default on purpose: no default, token, or wildcard
 * ever enables the surface for an unresolved session.
 */
export function resolveThreadMentionsEnabled(ctx: MentionsEnablementContext = {}): boolean {
  if (ctx.override !== undefined) return ctx.override;

  const account = (ctx.accountId ?? '').trim();
  if (!account) return false;

  if (ctx.defaultEnabled ?? THREAD_MENTIONS_ENABLED) return true;

  const source = ctx.enabledAccountIds ?? readEnabledMentionAccountsFromEnv();
  for (const raw of source) {
    const token = String(raw ?? '').trim();
    // ALL-ACCOUNTS ROLLOUT (2026-07-24): the literal '*' admits every RESOLVED
    // account — checked after the blank-account guard, so anonymous /
    // not-yet-resolved sessions stay fail-closed. Rollback = repo-var edit.
    if (token === '*' || token === account) return true;
  }
  return false;
}
